import React from 'react';
import { motion } from 'framer-motion';
import { Sparkles, Lightbulb, FileText } from 'lucide-react';
import { Theme } from '../../types/theme';

interface InitialChatScreenProps {
    userName?: string | null;
    companyName?: string;
    theme: Theme;
    onQuestionSelect: (question: string) => void;
}

export function getGreetingByUserTimezone(): string {
    const userTime = new Date();
    const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const hour = new Date().toLocaleString("en-US", {
        hour: "numeric",
        hour12: false,
        timeZone: userTimezone,
    });

    const currentHour = Number(hour);

    if (currentHour < 12) return "Good Morning";
    if (currentHour < 18) return "Good Afternoon";
    return "Good Evening";
}



const InitialChatScreen: React.FC<InitialChatScreenProps> = ({
    userName,
    companyName = "Deemsys",
    theme,
    onQuestionSelect,
}) => {
    const isDark = theme.mode === 'dark';

    const suggestions = [
        {
            icon: Lightbulb,
            color: '#facc15', // Light bulb yellow
            title: `About ${companyName}`,
            question: `Tell me about ${companyName} ?`
        },
        {
            icon: FileText,
            color: isDark ? '#a5b4fc' : '#6366f1',
            title: 'Industry Expertise',
            question: `How is ${companyName} progressing in the AI Solutions ?`
        }
    ];


    return (
        <div className="relative w-full flex-1 flex flex-col">
            {/* 🌟 Welcome Text */}
            <div className="flex flex-col items-center justify-start pt-10 md:pt-14 relative z-30 px-4 pointer-events-none">
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6 }}
                    className="text-center space-y-2"
                >
                    <h1
                        className="text-3xl md:text-2xl font-bold tracking-tight"
                        style={{ color: theme.textColor }}
                    >
                        {getGreetingByUserTimezone()}
                    </h1>

                    <p
                        className="text-base md:text-lg opacity-70 font-medium"
                        style={{ color: theme.textColor }}
                    >
                        How can I help you today?
                    </p>
                </motion.div>
            </div>
            <div className="w-full max-w-2xl flex flex-col md:flex-row justify-center gap-4 px-4 mt-auto mb-6 z-20 pointer-events-auto mx-auto border-none">
                {suggestions.map((item, index) => (
                    <motion.button
                        key={index}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5, delay: 0.2 + index * 0.1 }}
                        whileHover={{ scale: 1.02, y: -2 }}
                        whileTap={{ scale: 0.98 }}
                        onClick={() => onQuestionSelect(item.question)}
                        className={`
    flex items-center gap-2 px-3 py-2 rounded-2xl shadow-sm backdrop-blur-md
    transition-all duration-300 group w-full md:w-1/2 overflow-hidden relative
    ${isDark ? 'bg-gray-800/40 hover:bg-gray-800/60' : 'bg-white/60 hover:bg-white/80'}
`}

                    >
                        <div className={`absolute inset-0 rounded-3xl border opacity-20 pointer-events-none ${isDark ? 'border-white' : 'border-black'}`}></div>

                        <div className={`
                            p-2 rounded-2xl flex-shrink-0
                            ${isDark ? 'bg-gray-700/50' : 'bg-white/80'}
                            shadow-sm
                        `}>
                            <item.icon className="w-6 h-6" style={{ color: item.color }} />
                        </div>

                        <div className="flex flex-col items-start">
                            <span
                                className="font-bold text-lg tracking-tight"
                                style={{ color: theme.textColor }}
                            >
                                {item.title}
                            </span>
                        </div>
                    </motion.button>
                ))}
            </div>

            <div className="absolute bottom-12 w-full h-[60%] z-0 pointer-events-none overflow-hidden rounded-b-[2rem]">
                <div
                    className="absolute bottom-0 left-0 right-0 h-full w-full"
                    style={{
                        background: isDark
                            ? "radial-gradient(circle at 50% 100%, #4c1d95 0%, #1e1b4b 50%, transparent 100%)"
                            : "radial-gradient(circle at 50% 100%, #fce7f3 0%, #f3e8ff 40%, transparent 100%)",
                    }}
                />

                {/* Wave Layers */}
                <svg
                    className="absolute bottom-0 w-full h-full"
                    preserveAspectRatio="none"
                    viewBox="0 0 1440 400"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                >
                    <defs>
                        <linearGradient id="waveLightColor" x1="0%" y1="0%" x2="100%" y2="0%">
                            <stop offset="0%" stopColor="#f472b6" />
                            <stop offset="50%" stopColor="#a78bfa" />
                            <stop offset="100%" stopColor="#60a5fa" />
                        </linearGradient>

                        <linearGradient id="waveDarkColor" x1="0%" y1="0%" x2="100%" y2="0%">
                            <stop offset="0%" stopColor="#831843" />
                            <stop offset="50%" stopColor="#7c3aed" />
                            <stop offset="100%" stopColor="#1e40af" />
                        </linearGradient>

                        <linearGradient id="fadeMaskGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                            <stop offset="0%" stopColor="white" stopOpacity="1" />
                            <stop offset="100%" stopColor="white" stopOpacity="0" />
                        </linearGradient>

                        <mask id="fadeMask">
                            <rect x="0" y="0" width="1440" height="400" fill="url(#fadeMaskGradient)" />
                        </mask>
                    </defs>

                    <path
                        d="M0,160 Q360,120 720,160 T1440,140 V400 H0 Z"
                        fill={isDark ? "url(#waveDarkColor)" : "url(#waveLightColor)"}
                        mask="url(#fadeMask)"
                        opacity="0.4"
                    />

                    <path
                        d="M0,180 C240,260 560,80 1000,180 C1280,240 1400,160 1440,140 V400 H0 Z"
                        fill={isDark ? "url(#waveDarkColor)" : "url(#waveLightColor)"}
                        mask="url(#fadeMask)"
                        opacity="0.9"
                    />
                </svg>
            </div>
        </div>
    );
};

export default InitialChatScreen;